import { makeAutoObservable, runInAction } from 'mobx';

import type { WellIndexType, WellLog } from '../api/IWellDirectoryService';
import type { WellDirectoryService } from '../api/WellDirectoryService';
import type { WellDirectoryServiceStompClient, WellStructureChangeStatusMessage } from '../stompTypes';
import { arraysEqual, arraySum } from '../utils';

export class WellLogs {
  inProgress = false;

  list: WellLog[] = [];

  runNumbers: number[][] = [];

  wellId: number | null = null;

  wellboreId: number | null = null;

  constructor(
    private wellDirectoryService: WellDirectoryService,
    private wellDirectoryServiceStompClient: WellDirectoryServiceStompClient,
  ) {
    makeAutoObservable(this);
  }

  get allCurves() {
    return this.list.flatMap((log) => log.curves);
  }

  async fetchWellLogs(wellId: number, wellboreId: number, index: WellIndexType) {
    this.inProgress = true;

    try {
      const data = await this.wellDirectoryService.getLogs(wellId, wellboreId, index);

      runInAction(() => {
        this.clear();
        this.wellId = wellId;
        this.wellboreId = wellboreId;
        this.list = data.reduce<WellLog[]>((acc, curr) => {
          if (curr.curves.length > 0) {
            return acc.concat({ ...curr });
          }

          return acc;
        }, []);
        this.runNumbers = data.reduce<number[][]>((acc, log) => {
          if (!acc.some((numbers) => arraysEqual(numbers, log.runNumber))) {
            acc.push(log.runNumber);
          }

          return acc;
        }, []);
        this.runNumbers = this.runNumbers.sort((a, b) => arraySum(a) - arraySum(b));
      });

      this.wellDirectoryServiceStompClient.subscribe(`/wellObjects/${wellId}`, this.messageResolver);

      return data;
    } catch (e: unknown) {
      console.error('ErrorToFetch', e);
      throw e;
    } finally {
      runInAction(() => {
        this.inProgress = false;
      });
    }
  }

  fetchLog(logId: number) {
    if (!this.wellId || !this.wellboreId) {
      return Promise.reject('No well ID');
    }

    return this.wellDirectoryService.getLogById(this.wellId, this.wellboreId, logId);
  }

  messageResolver = (message: WellStructureChangeStatusMessage) => {
    if (message.entityType === 'LOG') {
      if (message.changeType === 'OBJECT_CREATE' || message.changeType === 'DATA_UPDATED') {
        this.fetchLog(message.entityId).then((log) => {
          this.updateLog(log);
        });
      } else if (message.changeType === 'OBJECT_DELETE') {
        this.list = this.list.filter((log) => log.id !== message.entityId);
      }
    }
  };

  updateLog(log: WellLog) {
    const index = this.list.findIndex((item) => item.id === log.id);

    if (index !== -1) {
      this.list[index] = log;
    } else {
      this.list.push(log);
    }
  }

  clear() {
    this.list = [];
    this.runNumbers = [];
    this.wellDirectoryServiceStompClient.unsubscribe(`/wellObjects/${this.wellId}`, this.messageResolver);
  }

  destroy() {
    this.clear();
  }
}
